import type { ReactNode } from "react";
import type { MessageChannel, TemplatePreview } from "@shared/modules/x-messagerie";
import { initials, prettyPhone } from "../lib/texte";

/**
 * Un message tel que le client le lit : bulle de texto reçue (ou envoyée, `sortant`), ou
 * carte de courriel avec son objet. Texte enroulé et largeur bornée (26 octobre 2026 :
 * l'aperçu partait sur une seule ligne qui dépassait l'écran).
 */
export function MessageBubble({
  channel,
  subject,
  body,
  sortant = false,
}: {
  channel: MessageChannel;
  subject?: string | null;
  body: string;
  sortant?: boolean;
}) {
  const classes = ["bulle"];
  if (channel === "email") classes.push("bulle--courriel");
  if (sortant) classes.push("bulle--sortant");
  return (
    <div className={classes.join(" ")}>
      {subject ? <div className="bulle__objet">{subject}</div> : null}
      <p className="bulle__texte">{body}</p>
    </div>
  );
}

/**
 * Aperçu d'un modèle : qui le recevrait (initiales, nom, adresse lisible, canal), la bulle,
 * puis les actions propres à l'aperçu (envoi d'essai) — visibles seulement quand l'aperçu
 * est ouvert, à la demande du propriétaire du 26 octobre 2026.
 */
export function PreviewBlock({
  preview,
  fallbackTo,
  actions,
}: {
  preview: TemplatePreview;
  fallbackTo: string;
  actions?: ReactNode;
}) {
  const adresse = preview.to
    ? preview.channel === "sms"
      ? prettyPhone(preview.to)
      : preview.to
    : fallbackTo;
  return (
    <div className="modele__preview" aria-live="polite">
      <div className="modele__preview-head">
        <span className="convo__avatar" aria-hidden="true">
          {initials(preview.toName)}
        </span>
        <div className="modele__preview-qui">
          <strong>{preview.toName ?? "Destinataire"}</strong>
          <span className="modele__preview-to">{adresse}</span>
        </div>
        <span className="modele__preview-canal">
          {preview.channel === "sms" ? "Texto" : "Courriel"}
        </span>
      </div>
      <MessageBubble channel={preview.channel} subject={preview.subject} body={preview.body} />
      {actions ? <div className="modele__preview-actions">{actions}</div> : null}
    </div>
  );
}
